/**
 * Copyright © 2025 Safar Glimpse (SafarGlimpse.com). All Rights Reserved.
 *
 * PROPRIETARY SOFTWARE - Unauthorized copying prohibited.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import DesignTokens from '../styles/DesignTokens';
import GlassmorphismTokens from '../styles/GlassmorphismTokens';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: ${DesignTokens.spacing.xl};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @supports not (backdrop-filter: blur(1px)) {
    background-image: none;
    background: #1e293b;
  }
`;

const Container = styled.div`
  max-width: 600px;
  text-align: center;
  width: 100%;
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border: ${GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius['2xl']};
  box-shadow: ${GlassmorphismTokens.shadow.glass};
  padding: ${DesignTokens.spacing['3xl']} ${DesignTokens.spacing.xl};

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.85);
  }
`;

const ErrorCode = styled.h1`
  font-size: 8rem;
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  margin: 0;
  line-height: 1;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 5rem;
  }
`;

const Title = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: white;
  margin: ${DesignTokens.spacing.md} 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${DesignTokens.typography.fontSize.xl};
  }
`;

const Description = styled.p`
  font-size: ${DesignTokens.typography.fontSize.base};
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: ${DesignTokens.spacing.xl};
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.md};
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled(Link)`
  display: inline-block;
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.xl};
  background: ${props => props.primary ? GlassmorphismTokens.gradient.blueCTA : 'transparent'};
  color: white;
  border: ${props => props.primary ? 'none' : GlassmorphismTokens.border.medium};
  border-radius: ${DesignTokens.borderRadius.md};
  text-decoration: none;
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  font-size: ${DesignTokens.typography.fontSize.base};
  transition: all ${DesignTokens.transition.normal} ease;
  box-shadow: ${props => props.primary ? GlassmorphismTokens.shadow.blueCTA : 'none'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? GlassmorphismTokens.shadow.blueCTAHover : GlassmorphismTokens.shadow.glass};
    background: ${props => props.primary ? GlassmorphismTokens.gradient.blueCTAHover : 'rgba(255, 255, 255, 0.1)'};
  }
`;

function NotFoundPage() {
  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | SafarGlimpse.com</title>
        <meta name="description" content="The page you're looking for doesn't exist. Return to explore heritage sites and famous destinations." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PageContainer>
        <Container>
          <ErrorCode>404</ErrorCode>
          <Title>Page Not Found</Title>
          <Description>
            The page you're looking for doesn't exist or has been moved.
            Let's get you back to exploring amazing heritage sites and destinations.
          </Description>
          <ButtonGroup>
            <Button primary="true" to="/">
              Go to Home
            </Button>
            <Button to="/places">
              Explore Places
            </Button>
            <Button to="/heritage">
              View Heritage Sites
            </Button>
          </ButtonGroup>
        </Container>
      </PageContainer>
    </>
  );
}

export default NotFoundPage;
